import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BulkUpdateReleaseItemsRequest, BulkUpdateReleaseItemsResponse, FileParameter, GetDaydreamSourceRequest, GetDaydreamSourceResponse, GetReleaseRequest, GetReleaseResponse, ListReleaseIssuesRequest,
  ListReleaseIssuesResponse, ListRenderedOutputsRequest, ListRenderedOutputsResponse, ListRenderingReleaseItemsRequest, ListRenderingReleaseItemsResponse,
  LoadProductUsageFromArtifactRequest,
  ProductName, Release, ReleaseIssue, ReleaseIssuePriority, ReleaseIssueStatus, ReleaseItem, ReleaseItemFieldToUpdate, ReleaseItemVersion, ReleaseStatus,
  RenderedOutput, RenderedOutputStatus, RenderReleaseStatus, SetReleaseItemViewedRequest, SetReleaseItemViewedResponse, SituViewWebApiClient,
  UpdateReleaseRequest, UpdateReleaseResponse
} from 'situview-webapi-client';
import { AuthService, BreadcrumbService, CompanyService, ConfigService, ConfirmDialogService, FacadeDownloadService, ProductNameService, ReleaseService, StateService, Utils } from 'src/app/services';
import { DaydreamService } from 'src/app/modules/situ-daydream/services/daydream.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ViewControls } from 'src/app/shared/models/view-controls';
import { PageEvent } from '@angular/material/paginator';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { GetMostPopularRenderedHomesByBuilderRequest, GetMostPopularRenderedHomesByBuilderResponse, IRendererPool, ListRendererPoolsRequest, SituAnalyticsWebApiClient, ViewMostPopularHomeLogItem } from 'situanalytics-webapi-client';
import { RenameDialogComponent } from 'src/app/components/rename-dialog/rename-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { interval, of, Subscription } from 'rxjs';
import { concatMap, takeWhile } from 'rxjs/operators';
import { ReleaseStyleTemplateDialogComponent, ReleaseStyleTemplateDialogComponentResultData } from '../release-style-template-dialog/release-style-template-dialog.component';
import { ReleaseIssueDialogComponent, ReleaseIssueDialogComponentResultData } from '../release-issue-dialog/release-issue-dialog.component';

@Component({
  selector: 'release-edit',
  templateUrl: './release-edit.component.html',
  styleUrls: ['./release-edit.component.scss']
})

export class ReleaseEditComponent extends SituBaseComponent implements AfterViewInit {
  viewControls = {
    pageIndex: 0,
    pageSize: 100,
    totalItems: 0,
    pageSizeOptions: [25, 50, 100]
  } as ViewControls;

  id: string = '';
  released: boolean = false;
  top100: boolean = false;
  data: Release | undefined;
  thumbnailData: string | undefined;
  releaseStatuses = ReleaseStatus;
  filteredReleaseItems: ReleaseItem[] = [];
  selectedReleaseItems: string[] = [];
  productNames = ProductName;
  renderReleaseStatuses = RenderReleaseStatus;
  availableStatuses: { value: ReleaseStatus, key: string, color: string }[] = [];
  logItems: ViewMostPopularHomeLogItem[] | undefined;
  scanningArtifactId: string | undefined;

  /* Renderer pools (situ-only) */
  readonly facadePriorityPoolName: string = 'facade-priority';
  rendererPools: IRendererPool[] = [];
  /* Renderer pools */

  /* Filters */
  searchTerm: string | undefined;
  selectedHome: string | undefined;
  selectedMasterFile: string | undefined;
  selectedOption: string | undefined;
  selectedStyleTemplate: string | undefined;
  selectedReleaseStatus: ReleaseStatus | undefined;
  selectedProductName: ProductName | undefined;
  commentsFilter: boolean = false;
  ticketFilter: boolean = false;
  standardFilter: boolean = false;
  draftFilter: boolean = false;
  hiResFilter: boolean = false;
  issueFilter: string | undefined;
  viewedFilter: boolean = false;
  /* Filters */

  updateReleaseItemValue: string | undefined;
  updateReleaseItemEnumValue: number | undefined;
  releaseItemFieldToUpdateOptions = ReleaseItemFieldToUpdate;
  releaseItemFieldToUpdate: ReleaseItemFieldToUpdate | undefined;

  situLinkBaseUrl: string | undefined;
  situPortalBaseUrl: string | undefined;

  /* Daydream source: the Daydream whose prompt this release's Daydream renders are made with */
  daydreamSourceInput: string = '';
  /** The release's saved source. */
  daydreamSource: GetDaydreamSourceResponse | undefined;
  /** The Daydream behind a pasted situlink, shown until it is used as the source or the entry is cancelled. */
  daydreamSourceCandidate: GetDaydreamSourceResponse | undefined;
  daydreamSourceEditing: boolean = false;
  daydreamSourceError: string | undefined;
  daydreamSourceLoading: boolean = false;
  releaseItemVersions = ReleaseItemVersion;
  /** Facade items whose Daydream is generating right now; kept current by the poll below. */
  daydreamGeneratingIds = new Set<string>();
  private daydreamPoll: Subscription | undefined;
  /* Daydream source */

  /* Query Parameters */
  readonly SearchParamName: string = "search";
  readonly HomeParamName: string = "home";
  readonly MasterFileParamName: string = "masterfile";
  readonly OptionsParamName: string = "options";
  readonly StyleTemplateParamName: string = "template";
  readonly RenderTypeParamName: string = "type";
  readonly StatusParamName: string = "status";
  readonly CommentsParamName: string = "comments";
  readonly TicketsParamName: string = "tickets";
  readonly StandardParamName: string = "standard";
  readonly DraftParamName: string = "draft";
  readonly HiResParamName: string = "hires";
  readonly IssueParamName: string = "issue";
  readonly ViewedParamName: string = "viewed";
  /* Query Parameters */

  private _loading = false;
  public get loading(): boolean { return this._loading; }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private situViewApiClient: SituViewWebApiClient,
    private releaseService: ReleaseService,
    private dialogService: ConfirmDialogService,
    private analyticsClient: SituAnalyticsWebApiClient,
    private dialog: MatDialog,
    private configService: ConfigService,
    private productNameService: ProductNameService,
    private facadeDownloadService: FacadeDownloadService,
    private companyService: CompanyService,
    private daydreamService: DaydreamService,
    private clipboard: Clipboard) {
    super(authService, stateService);
    this.id = this.route.snapshot.params['id'];
    if (this.id.toLowerCase() === "complete") this.released = true;
    if (this.id.toLowerCase() === "top100") {
      this.released = true;
      this.top100 = true;
    }
    this.situLinkBaseUrl = this.configService.config.situLinkBaseUrl;
    this.situPortalBaseUrl = this.configService.config.situPortalBaseUrl;

    if (this.route.snapshot.queryParams[this.SearchParamName]) {
      this.searchTerm = this.route.snapshot.queryParams[this.SearchParamName];
    }

    if (this.route.snapshot.queryParams[this.HomeParamName]) {
      this.selectedHome = this.route.snapshot.queryParams[this.HomeParamName];
    }

    if (this.route.snapshot.queryParams[this.MasterFileParamName]) {
      this.selectedMasterFile = this.route.snapshot.queryParams[this.MasterFileParamName];
    }

    if (this.route.snapshot.queryParams[this.OptionsParamName]) {
      this.selectedOption = this.route.snapshot.queryParams[this.OptionsParamName];
    }

    if (this.route.snapshot.queryParams[this.StyleTemplateParamName]) {
      this.selectedStyleTemplate = this.route.snapshot.queryParams[this.StyleTemplateParamName];
    }

    if (this.route.snapshot.queryParams[this.RenderTypeParamName]) {
      this.selectedProductName = Number(this.route.snapshot.queryParams[this.RenderTypeParamName]) as ProductName;
    }

    if (this.route.snapshot.queryParams[this.StatusParamName]) {
      this.selectedReleaseStatus = Number(this.route.snapshot.queryParams[this.StatusParamName]) as ReleaseStatus;
    }

    if (this.route.snapshot.queryParams[this.CommentsParamName]) {
      this.commentsFilter = this.route.snapshot.queryParams[this.CommentsParamName] === 'true';
    }

    if (this.route.snapshot.queryParams[this.TicketsParamName]) {
      this.ticketFilter = this.route.snapshot.queryParams[this.TicketsParamName] === 'true';
    }

    if (this.route.snapshot.queryParams[this.StandardParamName]) {
      this.standardFilter = this.route.snapshot.queryParams[this.StandardParamName] === 'true';
    }

    if (this.route.snapshot.queryParams[this.DraftParamName]) {
      this.draftFilter = this.route.snapshot.queryParams[this.DraftParamName] === 'true';
    }

    if (this.route.snapshot.queryParams[this.HiResParamName]) {
      this.hiResFilter = this.route.snapshot.queryParams[this.HiResParamName] === 'true';
    }
    
    if (this.route.snapshot.queryParams[this.ViewedParamName]) {
      this.viewedFilter = this.route.snapshot.queryParams[this.ViewedParamName] === 'true';
    }

    if (this.route.snapshot.queryParams[this.IssueParamName]) {
      this.issueFilter = this.route.snapshot.queryParams[this.IssueParamName];
      this.showReleaseIssues = true;
    }
  }

  protected onSelectedCompanyChange(): void {
    this.load();
    if (this.released) this.loadReleases();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadRendererPools();
  }

  // Override to refresh colors when theme changes
  protected onThemeChange(): void {
    this.updateAvailableStatuses();
  }

  getSetStatusTooltip(): string {
    if (this.selectedReleaseItems?.length === 0) {
      return 'Set status - Select release items first';
    }
    if (this.availableStatuses?.length === 0) {
      return 'Set status - No status transitions available for selected items';
    }
    return 'Set status';
  }

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  /* auto paging */
  pageSize = 8;
  pageLength = 8;
  @ViewChild('showMoreProductsMarker') showMoreProductsMarker: ElementRef | undefined;
  private observer: IntersectionObserver | undefined;
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.hasMoreProductsToShow()) {
          this.showMoreProducts();
        }
      });
    });

    if (this.showMoreProductsMarker?.nativeElement) {
      this.observer.observe(this.showMoreProductsMarker.nativeElement);
    }
  }

  showMoreProducts() {
    const incrementSize = 2;
    const intervalTime = 200;
    const targetPageLength = this.pageLength + this.pageSize;
    this._subscriptions.push(interval(intervalTime).pipe(
      takeWhile(() => this.pageLength < targetPageLength && this.pageLength < this.filteredReleaseItems.length),
      concatMap(() => {
        const nextPageLength = Math.min(this.pageLength + incrementSize, targetPageLength, this.filteredReleaseItems.length);
        return of(nextPageLength);
      })
    ).subscribe((nextPageLength) => {
      this.pageLength = nextPageLength;
    }));
  }

  hasMoreProductsToShow(): boolean {
    return this.filteredReleaseItems.length > this.pageLength;
  }
  /* auto paging */

  approveForLive(releaseItem: ReleaseItem) {
    if (!this.selectedCompany?.id || !releaseItem?.id || releaseItem.releaseStatus === ReleaseStatus.Live) {
      return;
    }

    this.dialogService.open({
      title: 'Approve for Live?',
      message: 'Do you approve this release item for Live?',
      cancelText: 'No',
      confirmText: 'Yes',
      actionIcon: 'check',
      actionColour: 'accent'
    } as ConfirmDialogData);
    this._subscriptions.push(this.dialogService.confirmed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.releaseService.SetStatusReleaseItems(this.selectedCompany!.id!, [releaseItem.id!], ReleaseStatus.ApprovedForLive)
            .then((response: BulkUpdateReleaseItemsResponse) => {
              if (response.success) {
                this.load();
                this.clearValues();
              } else {
                this.snackBar.open(`Could not update the release items. Refresh Page.`, 'OK', { duration: 2000 });
              }
            })
            .catch((err: any) => {
              this.snackBar.open(`Could not update the release items. Refresh Page.`, 'OK', { duration: 2000 });
            });
        } else {
          this.loading = false;
        }
      }));
  }

  isExpired(releaseDate: Date) {
    return releaseDate < new Date();
  }

  getStatusColor(status: ReleaseStatus) {
    return this.releaseService.getStatusColor(status, this.isDarkMode);
  }

  onChartClick(label: string): void {
    // The chart component emits the label that was clicked
    // Find the status that matches this label
    const statusEntry = this.statusPercentages.find(sp => this.releaseStatuses[sp.status] === label);
    if (statusEntry) {
      this.filterReleaseStatus(statusEntry.status);
    }
  }

  getTotalStatusCount(): number {
    return this.statusPercentages.reduce((total, status) => total + status.count, 0);
  }

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName as any)?.productColor;
  }

  getLivePercentage() {
    if (this.data?.releaseItems) {
      let totalItems = this.data.releaseItems.length;
      if (totalItems === 0) {
        return 0;
      }
      let liveItems = this.data.releaseItems.filter(x => x.releaseStatus === ReleaseStatus.Live).length;
      return Math.round((liveItems / totalItems) * 100);
    } else {
      return 0;
    }
  }

  edit(field: string) {
    if (!field) return;

    const dialogRef = this.dialog.open(RenameDialogComponent, {
        width: '350px',
        height: '60%',
        data: { 
            existingName: field === 'name' ? this.data?.name : this.data?.description
        }
    });

    this._subscriptions.push(dialogRef.afterClosed().subscribe(name => {
        if (name !== undefined) {
          const request = this.buildUpdateReleaseRequest();
            if (field === 'name') {
              request.name = name;
            } else {
              request.description = name;
            }

            this.situViewApiClient.updateRelease(request)
                .then((response: UpdateReleaseResponse) => {
                    if (response.success) {
                        this.load();
                        this.snackBar.open(`Updated release!`, 'OK', { duration: 2000 });
                    } else {
                        this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
                    }
                })
                .catch((err: any) => {
                    this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
                });
        }
    }));
  }

  addDays() {
    if (this.data) {
      const request = this.buildUpdateReleaseRequest();

      // If currently Released, set it back to InFlight to re-enable
      request.renderReleaseStatus = this.data?.renderReleaseStatus === RenderReleaseStatus.Released
        ? RenderReleaseStatus.InFlight
        : this.data?.renderReleaseStatus;

      const currentDate = this.data?.releaseDate ? this.data.releaseDate : new Date();
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 10);
      request.releaseDate = newDate;

      this.situViewApiClient.updateRelease(request)
          .then((response: UpdateReleaseResponse) => {
              if (response.success) {
                  this.load();
                  const message = this.data?.renderReleaseStatus === RenderReleaseStatus.Released 
                    ? `Release re-enabled!` 
                    : `Release extended!`;
                  this.snackBar.open(message, 'OK', { duration: 2000 });
              } else {
                  this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
              }
          })
          .catch((err: any) => {
              this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
          });
    }
  }

  togglePreRelease() {
    if (this.data) {
      const request = this.buildUpdateReleaseRequest();
      request.renderReleaseStatus = this.data?.renderReleaseStatus === RenderReleaseStatus.PreRelease ? RenderReleaseStatus.InFlight : RenderReleaseStatus.PreRelease;

      this.situViewApiClient.updateRelease(request)
          .then((response: UpdateReleaseResponse) => {
              if (response.success) {
                  this.load();
                  this.snackBar.open(`Updated release!`, 'OK', { duration: 2000 });
              } else {
                  this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
              }
          })
          .catch((err: any) => {
              this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
          });
    }
  }

  markAsReleased() {
    if (this.data) {
      this.dialogService.open({
        title: 'Mark as Released?',
        message: 'Are you sure you want to mark this release as released? This action cannot be undone.',
        cancelText: 'Cancel',
        confirmText: 'Mark as Released',
        actionIcon: 'check_circle',
        actionColour: 'accent'
      } as ConfirmDialogData);
      
      this._subscriptions.push(this.dialogService.confirmed()
        .subscribe(confirmed => {
          if (confirmed && this.data) {
            const request = this.buildUpdateReleaseRequest();
            request.renderReleaseStatus = RenderReleaseStatus.Released;

            this.situViewApiClient.updateRelease(request)
                .then((response: UpdateReleaseResponse) => {
                    if (response.success) {
                        this.load();
                        this.snackBar.open(`Release marked as released!`, 'OK', { duration: 2000 });
                    } else {
                        this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
                    }
                })
                .catch((err: any) => {
                    this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
                });
          }
        }));
    }
  }  
  toggleRaytraced() {
    if (this.data) {
      const request = this.buildUpdateReleaseRequest();
      request.situRaytracedPlus = !this.data?.situRaytracedPlus;

      this.situViewApiClient.updateRelease(request)
          .then((response: UpdateReleaseResponse) => {
              if (response.success) {
                  this.load();
                  this.snackBar.open(`Updated raytraced setting!`, 'OK', { duration: 2000 });
              } else {
                  this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
              }
          })
          .catch((err: any) => {
              this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
          });
    }
  }
  toggleSituPlan() {
    if (this.data) {
      const request = this.buildUpdateReleaseRequest();
      request.situPlan = !this.data?.situPlan;

      this.situViewApiClient.updateRelease(request)
          .then((response: UpdateReleaseResponse) => {
              if (response.success) {
                  this.load();
                  this.snackBar.open(`Updated situPLAN setting!`, 'OK', { duration: 2000 });
              } else {
                  this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
              }
          })
          .catch((err: any) => {
              this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
          });
    }
  }
  toggleSituRender() {
    if (this.data) {
      const request = this.buildUpdateReleaseRequest();
      request.situRender = !this.data?.situRender;

      this.situViewApiClient.updateRelease(request)
          .then((response: UpdateReleaseResponse) => {
              if (response.success) {
                  this.load();
                  this.snackBar.open(`Updated situRENDER setting!`, 'OK', { duration: 2000 });
              } else {
                  this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
              }
          })
          .catch((err: any) => {
              this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
          });
    }
  }

  toggleSkipRaytraced() {
    if (this.data) {
      const request = this.buildUpdateReleaseRequest();
      request.skipRaytraced = !this.data?.skipRaytraced;

      this.situViewApiClient.updateRelease(request)
          .then((response: UpdateReleaseResponse) => {
              if (response.success) {
                  this.load();
                  this.snackBar.open(`Updated Skip Raytraced setting!`, 'OK', { duration: 2000 });
              } else {
                  this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
              }
          })
          .catch((err: any) => {
              this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
          });
    }
  }

  /**
   * True when the current release belongs to Burbank. Gates the Express Facade shortcut.
   */
  get isBurbank(): boolean {
    const code = this.selectedCompany?.code?.toLowerCase() ?? '';
    const name = this.selectedCompany?.name?.toLowerCase() ?? '';
    return code.includes('burbank') || name.includes('burbank');
  }

  /**
   * Pool names shown as toggle pills: the managed pools plus any names already
   * assigned to the release that aren't managed pools (so they can still be toggled off).
   * Names are trimmed and de-duplicated case-insensitively, so "Release", "Release " and
   * "release" all collapse to a single pill.
   */
  get rendererPoolOptions(): string[] {
    // Managed pools first, so their canonical name wins when a stored value differs only by
    // case/whitespace (e.g. managed "Release " vs stored "Release").
    return this.dedupePoolNames([
      ...this.rendererPools.map(pool => pool.name ?? ''),
      ...this.parsePoolNames(this.data?.allowedRendererPools)
    ]);
  }

  /** Identity key for a pool: trimmed + lower-cased, so "Release", "Release " and "release" all match. */
  private poolKey(name: string | undefined): string {
    return (name ?? '').trim().toLowerCase();
  }

  /**
   * Resolve a pool name to the managed pool's exact stored name when one matches, so the value we
   * persist matches the renderer pool record verbatim (the backend matches on names). Falls back to
   * the trimmed input for ad-hoc names with no managed pool.
   */
  private canonicalPoolName(name: string): string {
    const key = this.poolKey(name);
    const match = this.rendererPools.find(pool => this.poolKey(pool.name) === key);
    return match?.name ?? (name ?? '').trim();
  }

  /** De-duplicate by pool key, preserving the first-seen (canonical) form; drops blanks. */
  private dedupePoolNames(names: string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    names.forEach(raw => {
      const key = this.poolKey(raw);
      if (!key || seen.has(key)) return;
      seen.add(key);
      result.push(raw);
    });
    return result;
  }

  private loadRendererPools(): void {
    const request = new ListRendererPoolsRequest();
    request.pageNumber = 1;
    request.pageSize = 100;

    this.analyticsClient.listRendererPools(request)
      .then(response => {
        this.rendererPools = (response.success && response.items) ? response.items : [];
      })
      .catch(() => {
        this.rendererPools = [];
      });
  }

  /**
   * Parse the allowedRendererPools string into trimmed, non-empty names. Pools are stored
   * semicolon-separated (e.g. "BURBANK;FACADE-PRIORITY").
   */
  private parsePoolNames(value: string | undefined): string[] {
    return (value ?? '')
      .split(';')
      .map(name => name.trim())
      .filter(name => name.length > 0);
  }

  /** Persist the given pool names to the release as a comma-separated string. */
  private saveRendererPools(poolNames: string[], successMessage: string): void {
    if (!this.data) return;

    const request = this.buildUpdateReleaseRequest();
    // Store semicolon-separated (the format the render backend expects, e.g. "BURBANK;FACADE-PRIORITY").
    // Normalize (trim + de-dupe) so duplicates never accumulate, and always send a concrete string
    // ('' when empty) — an undefined value is dropped from the serialized request.
    request.allowedRendererPools = this.dedupePoolNames(poolNames).join(';');

    this.situViewApiClient.updateRelease(request)
      .then((response: UpdateReleaseResponse) => {
        if (response.success) {
          this.load();
          this.snackBar.open(successMessage, 'OK', { duration: 2000 });
        } else {
          this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
        }
      })
      .catch(() => {
        this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
      });
  }

  /** True when the given pool is currently assigned to this release (matched by key). */
  isPoolSelected(poolName: string): boolean {
    const key = this.poolKey(poolName);
    return this.parsePoolNames(this.data?.allowedRendererPools).some(name => this.poolKey(name) === key);
  }

  /** Toggle a single renderer pool on/off for this release, keeping the other pools intact. */
  togglePool(poolName: string): void {
    const key = this.poolKey(poolName);
    const current = this.parsePoolNames(this.data?.allowedRendererPools);
    if (this.isPoolSelected(poolName)) {
      const remaining = current.filter(name => this.poolKey(name) !== key);
      this.saveRendererPools(remaining, `Removed "${poolName.trim()}" renderer pool.`);
    } else {
      this.saveRendererPools([...current, this.canonicalPoolName(poolName)], `Added "${poolName.trim()}" renderer pool.`);
    }
  }

  /** True when the facade-priority pool is currently assigned to this release. */
  get isFacadePriorityOn(): boolean {
    const key = this.poolKey(this.facadePriorityPoolName);
    return this.parsePoolNames(this.data?.allowedRendererPools).some(name => this.poolKey(name) === key);
  }

  /** Burbank Express Facade toggle — adds or removes the facade-priority pool, keeping other pools intact. */
  toggleFacadePriorityPool(): void {
    const key = this.poolKey(this.facadePriorityPoolName);
    const current = this.parsePoolNames(this.data?.allowedRendererPools);
    if (this.isFacadePriorityOn) {
      const remaining = current.filter(name => this.poolKey(name) !== key);
      this.saveRendererPools(remaining, 'Express Facade disabled — facade-priority pool removed.');
    } else {
      this.saveRendererPools([...current, this.canonicalPoolName(this.facadePriorityPoolName)], 'Express Facade enabled — facade-priority pool added!');
    }
  }

  /* Daydream source */

  /** The id a pasted situlink carries: its last path segment, or a bare id. */
  parseSituLinkId(text: string | undefined): string | undefined {
    const match = (text ?? '').match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi);
    return match ? match[match.length - 1].toLowerCase() : undefined;
  }

  /** The saved source, shown on load. */
  loadDaydreamSource(): void {
    const id = this.data?.daydreamSourceRenderedOutputId;
    this.daydreamSource = undefined;
    this.daydreamSourceCandidate = undefined;
    this.daydreamSourceEditing = false;
    this.daydreamSourceError = undefined;
    this.daydreamSourceInput = '';
    if (id) {
      this.fetchDaydreamSource(id, response => this.daydreamSource = response);
    }
  }

  /** Opens the situlink entry in place of the saved source. */
  editDaydreamSource(): void {
    this.daydreamSourceInput = '';
    this.daydreamSourceCandidate = undefined;
    this.daydreamSourceError = undefined;
    this.daydreamSourceEditing = true;
  }

  cancelDaydreamSourceEdit(): void {
    this.daydreamSourceEditing = false;
    this.daydreamSourceCandidate = undefined;
    this.daydreamSourceError = undefined;
    this.daydreamSourceInput = '';
  }

  /** Looks up whatever situlink was pasted. */
  previewDaydreamSource(): void {
    const id = this.parseSituLinkId(this.daydreamSourceInput);
    this.daydreamSourceCandidate = undefined;
    if (!id) {
      this.daydreamSourceError = 'Paste a Daydream situlink.';
      return;
    }
    this.fetchDaydreamSource(id, response => this.daydreamSourceCandidate = response);
  }

  private fetchDaydreamSource(renderedOutputId: string, found: (response: GetDaydreamSourceResponse) => void): void {
    const request = new GetDaydreamSourceRequest();
    request.renderedOutputId = renderedOutputId;
    this.daydreamSourceLoading = true;
    this.daydreamSourceError = undefined;
    this.situViewApiClient.getDaydreamSource(request)
      .then((response: GetDaydreamSourceResponse) => {
        if (response?.success) {
          found(response);
        } else {
          this.daydreamSourceError = response?.errorMessages?.join(' ') || 'That situlink is not a Daydream you can use.';
        }
      })
      .catch((err: any) => {
        // the API answers 401 for a Daydream outside the caller's group
        this.daydreamSourceError = err?.status === 401 ? 'That Daydream belongs to a company outside your group.' : 'That situlink is not a Daydream you can use.';
      })
      .finally(() => this.daydreamSourceLoading = false);
  }

  /** Copies the saved source's situlink, as the Daydream page's share button does. */
  shareDaydreamSource(): void {
    const id = this.data?.daydreamSourceRenderedOutputId;
    if (!id) {
      return;
    }
    this.clipboard.copy(this.daydreamService.situLinkUrl(id));
    this.snackBar.open('Copied situLINK to clipboard!', 'OK', { duration: 2000 });
  }

  /** Makes the previewed Daydream the release's source; the entry closes when the release reloads. */
  saveDaydreamSource(): void {
    if (!this.data || !this.daydreamSourceCandidate?.id) {
      return;
    }
    const request = this.buildUpdateReleaseRequest();
    request.daydreamSourceRenderedOutputId = this.daydreamSourceCandidate.id;
    this.situViewApiClient.updateRelease(request)
      .then((response: UpdateReleaseResponse) => {
        if (response.success) {
          this.load();
          this.snackBar.open(`Daydream source saved!`, 'OK', { duration: 2000 });
        } else {
          this.snackBar.open(response.errorMessages?.join(' ') || `Error updating release!`, 'OK', { duration: 4000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
      });
  }

  clearDaydreamSource(): void {
    if (!this.data) {
      return;
    }
    const request = this.buildUpdateReleaseRequest();
    request.clearDaydreamSource = true;
    this.situViewApiClient.updateRelease(request)
      .then((response: UpdateReleaseResponse) => {
        if (response.success) {
          this.load();
          this.snackBar.open(`Daydream source removed.`, 'OK', { duration: 2000 });
        } else {
          this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
      });
  }

  daydreamSourceImageUrl(source: GetDaydreamSourceResponse | undefined = this.daydreamSource, width: number = 320): string | undefined {
    return source?.artifactId && source.imageFileName ? this.daydreamService.imageUrl(source.artifactId, source.imageFileName, width) : undefined;
  }

  /* Daydreams on facade items: asked for explicitly once the facade is rendered, never automatically */

  canRequestDaydream(item: ReleaseItem): boolean {
    return !this.released
      && item?.productName === ProductName.Facade
      && !!item?.latestVersion
      && !!this.data?.daydreamSourceRenderedOutputId;
  }

  isDaydreamGenerating(item: ReleaseItem): boolean {
    return !!item?.id && this.daydreamGeneratingIds.has(item.id);
  }

  /** The Daydream in the item's slot is done and can be viewed or shared. A failed one keeps its slot but has no image. */
  hasDaydream(item: ReleaseItem): boolean {
    return !!item?.daydreamVersion && item.daydreamStatus === RenderedOutputStatus.Complete && !this.isDaydreamGenerating(item);
  }

  /** The Daydream in the item's slot failed (or was cancelled): there is nothing to view, and it can be asked for again. */
  isDaydreamFailed(item: ReleaseItem): boolean {
    return !!item?.daydreamVersion && !this.isDaydreamGenerating(item)
      && (item.daydreamStatus === RenderedOutputStatus.Failed || item.daydreamStatus === RenderedOutputStatus.Cancelled);
  }

  daydreamTooltip(item: ReleaseItem): string {
    if (!this.data?.daydreamSourceRenderedOutputId) return 'Set the release\'s Daydream source first';
    if (!item?.latestVersion) return 'Render the facade first';
    if (this.isDaydreamGenerating(item)) return 'Daydream generating…';
    if (item?.daydreamRequested) return 'Daydream requested — waiting to be generated';
    if (this.isDaydreamFailed(item)) return 'The Daydream failed — render it again';
    if (item?.daydreamVersion) return 'Render the Daydream again (replaces the current one)';
    return 'Render a Daydream of this facade';
  }

  /* Daydream progress: while any facade here has a Daydream requested or generating, the page polls and
     refreshes those rows as each one finishes. The poll stops itself once nothing is pending. */

  private hasPendingDaydreams(): boolean {
    return !!this.data?.releaseItems?.some(x => x.daydreamRequested) || this.daydreamGeneratingIds.size > 0;
  }

  private watchDaydreams(): void {
    if (this.released || this.top100) {
      return;
    }
    this.checkDaydreams(false).then(() => {
      if (this.hasPendingDaydreams() && !this.daydreamPoll) {
        this.daydreamPoll = interval(10000).subscribe(() => this.checkDaydreams(true));
        this._subscriptions.push(this.daydreamPoll);
      }
    });
  }

  private stopDaydreamPoll(): void {
    this.daydreamPoll?.unsubscribe();
    this.daydreamPoll = undefined;
  }

  /** One look: which Daydreams are generating, and (on a poll tick) the rows' current Daydream fields. */
  private async checkDaydreams(refreshRows: boolean): Promise<void> {
    try {
      const request = new ListRenderingReleaseItemsRequest();
      request.releaseId = this.id;
      const response = await this.situViewApiClient.listRenderingReleaseItems(request);
      if (response.success) {
        this.daydreamGeneratingIds = new Set((response.daydreamItems ?? []).map(x => x.id!));
      }
      if (refreshRows) {
        await this.refreshDaydreamRows();
      }
    } catch (err) {
      console.error('Error checking Daydream progress:', err);
    }
    if (!this.hasPendingDaydreams()) {
      this.stopDaydreamPoll();
    }
  }

  /** Merges the Daydream fields into the rows in place, so selection, filters and paging stay as they are. */
  private async refreshDaydreamRows(): Promise<void> {
    if (!this.data?.releaseItems?.length) {
      return;
    }
    const request = new GetReleaseRequest();
    request.releaseId = this.id;
    request.companyId = this.selectedCompany?.id;
    const response = await this.situViewApiClient.getRelease(request);
    if (!response.success || !response.release?.releaseItems) {
      return;
    }
    const latest = new Map(response.release.releaseItems.map(x => [x.id, x]));
    for (const row of this.data.releaseItems) {
      const current = latest.get(row.id);
      if (current) {
        row.daydreamRequested = current.daydreamRequested;
        row.daydreamVersion = current.daydreamVersion;
        row.daydreamStatus = current.daydreamStatus;
        row.approvedVersion = current.approvedVersion;
        row.releaseStatus = current.releaseStatus;
        row.approvedByName = current.approvedByName;
      }
    }
  }

  /* Which version is served: the 2k, the 4k and the Daydream are one family, so exactly one of them
     (or none, before the item is Live) is production. A tick per version; the served one is highlighted. */

  isServed(item: ReleaseItem, versionId: string | undefined): boolean {
    return !!versionId && item?.approvedVersion === versionId;
  }

  versionTooltip(item: ReleaseItem, versionId: string | undefined, name: string): string {
    if (this.isServed(item, versionId)) {
      return `${name} is live` + (item?.approvedByName ? ` — approved by ${item.approvedByName}` : '');
    }
    if (item?.releaseStatus === ReleaseStatus.Live || item?.approvedVersion) {
      return `Serve the ${name} instead`;
    }
    return name === '2k facade' ? 'Approve For Live' : `Approve the ${name} for Live`;
  }

  /** The 2k tick: on an item not yet Live this is the existing approval; on a Live item it serves the 2k again. */
  approveStandard(item: ReleaseItem): void {
    if (item?.releaseStatus === ReleaseStatus.Live || item?.approvedVersion) {
      this.approveVersion(item, ReleaseItemVersion.Standard, '2k facade');
    } else {
      this.approveForLive(item);
    }
  }

  approveVersion(item: ReleaseItem, version: ReleaseItemVersion, name: string): void {
    if (!item?.id) {
      return;
    }
    const goingLive = item.releaseStatus !== ReleaseStatus.Live;
    this.dialogService.open({
      title: goingLive ? `Approve the ${name} for Live?` : `Serve the ${name}?`,
      message: goingLive
        ? `The ${name} will be served to the customer site and the item will go Live.`
        : `The ${name} will be served to the customer site in place of the current version.`,
      cancelText: 'No',
      confirmText: 'Yes',
      actionIcon: 'check',
      actionColour: 'accent'
    } as ConfirmDialogData);
    this._subscriptions.push(this.dialogService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.sendApproveVersion([item.id!], version, name);
      }
    }));
  }

  private sendApproveVersion(releaseItemIds: string[], version: ReleaseItemVersion, name: string): void {
    const request = new BulkUpdateReleaseItemsRequest();
    request.companyId = this.selectedCompany?.id;
    request.releaseItemIds = releaseItemIds;
    request.releaseItemFieldToUpdate = ReleaseItemFieldToUpdate.ApproveVersion;
    request.version = version;
    this.situViewApiClient.bulkUpdateReleaseItems(request)
      .then((response: BulkUpdateReleaseItemsResponse) => {
        if (response.success) {
          this.refreshDaydreamRows();
          const skipped = response.skipped?.length ?? 0;
          this.snackBar.open(skipped ? `Skipped: ${response.skipped!.join(' ')}` : `The ${name} is now live.`, 'OK', { duration: skipped ? 5000 : 2000 });
        } else {
          this.snackBar.open(response.errorMessages?.join(' ') || 'Could not update the release item.', 'OK', { duration: 4000 });
        }
      })
      .catch(() => {
        this.snackBar.open('Could not update the release item.', 'OK', { duration: 2000 });
      });
  }

  /* Approving the selected items' Daydreams for Live, through the bulk-update bar. Only the facades with a
     Daydream not yet live are sent; the rest are skipped and counted in the confirmation. The server still
     skips any Daydream that is not Complete and reports why. */

  /** Items per request: each one is promoted in turn on the server, so one request for thousands would time out. */
  private static readonly approveDaydreamsChunkSize = 100;
  daydreamsApproving = false;
  private releaseItemsById: Map<string | undefined, ReleaseItem> | undefined;
  private releaseItemsByIdSource: ReleaseItem[] | undefined;
  private releaseFacadeCount = 0;

  /** Indexes the release's items once per load; template checks run on every change detection. */
  private indexReleaseItems(): void {
    const items = this.data?.releaseItems;
    if (this.releaseItemsByIdSource !== items) {
      this.releaseItemsByIdSource = items;
      this.releaseItemsById = new Map((items ?? []).map(x => [x.id, x]));
      this.releaseFacadeCount = (items ?? []).filter(x => x.productName === ProductName.Facade).length;
    }
  }

  /** The release holds facades, whatever the product filter shows. */
  releaseHasFacades(): boolean {
    this.indexReleaseItems();
    return this.releaseFacadeCount > 0;
  }

  private selectedItems(): ReleaseItem[] {
    this.indexReleaseItems();
    return (this.selectedReleaseItems ?? [])
      .map(id => this.releaseItemsById!.get(id))
      .filter((x): x is ReleaseItem => !!x);
  }

  /** Sorts the selection: the items whose Daydream can be approved, and why each of the rest will be skipped. */
  private daydreamSelection(): { toApprove: string[]; notFacade: number; noDaydream: number; alreadyLive: number } {
    const result = { toApprove: [] as string[], notFacade: 0, noDaydream: 0, alreadyLive: 0 };
    for (const item of this.selectedItems()) {
      if (item.productName !== ProductName.Facade) result.notFacade++;
      else if (!this.hasDaydream(item)) result.noDaydream++;
      else if (this.isServed(item, item.daydreamVersion)) result.alreadyLive++;
      else result.toApprove.push(item.id!);
    }
    return result;
  }

  /** e.g. "160 facades with no Daydream and 40 non-facade items"; empty when nothing is skipped. */
  private daydreamSkipSummary(selection: { notFacade: number; noDaydream: number; alreadyLive: number }): string {
    const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
    const parts = [
      selection.noDaydream ? plural(selection.noDaydream, 'facade with no Daydream', 'facades with no Daydream') : '',
      selection.alreadyLive ? plural(selection.alreadyLive, 'Daydream already live', 'Daydreams already live') : '',
      selection.notFacade ? plural(selection.notFacade, 'non-facade item', 'non-facade items') : ''
    ].filter(x => x);
    return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts.join('');
  }

  canApproveDaydreams(): boolean {
    return !this.daydreamsApproving && this.daydreamSelection().toApprove.length > 0;
  }

  approveDaydreamsTooltip(): string {
    if (this.daydreamsApproving) return 'Approving Daydreams…';
    if (!this.selectedReleaseItems?.length) return 'Approve Daydreams for Live';
    const selection = this.daydreamSelection();
    const count = selection.toApprove.length;
    if (!count) return `Nothing to approve: ${this.daydreamSkipSummary(selection)}`;
    const skips = this.daydreamSkipSummary(selection);
    return `Approve ${count} Daydream${count === 1 ? '' : 's'} for Live` + (skips ? ` (skipping ${skips})` : '');
  }

  approveDaydreamsConfirm(): void {
    if (!this.canApproveDaydreams()) {
      return;
    }
    const selection = this.daydreamSelection();
    const ids = selection.toApprove;
    const skips = this.daydreamSkipSummary(selection);
    this.dialogService.open({
      title: `Approve ${ids.length} Daydream${ids.length === 1 ? '' : 's'} for Live?`,
      message: `Each facade's Daydream will be served to the customer site in place of its current version. Facades that are not Live yet will go Live.`
        + (skips ? ` Skipped: ${skips}.` : ''),
      cancelText: 'Cancel',
      confirmText: 'Approve',
      actionIcon: 'verified',
      actionColour: 'accent'
    } as ConfirmDialogData);
    this._subscriptions.push(this.dialogService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.sendApproveDaydreams(ids);
      }
    }));
  }

  /** Sends the ids in chunks, one after another, and sums what was skipped. Stops at the first chunk that fails. */
  private async sendApproveDaydreams(releaseItemIds: string[]): Promise<void> {
    const chunkSize = ReleaseEditComponent.approveDaydreamsChunkSize;
    const skipped: string[] = [];
    let sent = 0;
    let failure: string | undefined;
    this.daydreamsApproving = true;
    try {
      for (let start = 0; start < releaseItemIds.length; start += chunkSize) {
        if (releaseItemIds.length > chunkSize) {
          this.snackBar.open(`Approving Daydreams… ${start} of ${releaseItemIds.length}`);
        }
        const request = new BulkUpdateReleaseItemsRequest();
        request.companyId = this.selectedCompany?.id;
        request.releaseItemIds = releaseItemIds.slice(start, start + chunkSize);
        request.releaseItemFieldToUpdate = ReleaseItemFieldToUpdate.ApproveVersion;
        request.version = ReleaseItemVersion.Daydream;
        try {
          const response = await this.situViewApiClient.bulkUpdateReleaseItems(request);
          if (!response.success) {
            failure = response.errorMessages?.join(' ') || 'Could not approve the Daydreams.';
            break;
          }
          skipped.push(...(response.skipped ?? []));
          sent += request.releaseItemIds.length;
        } catch {
          failure = 'Could not approve the Daydreams.';
          break;
        }
      }
    } finally {
      this.daydreamsApproving = false;
    }

    const approved = sent - skipped.length;
    const parts = [`${approved} Daydream${approved === 1 ? '' : 's'} approved for Live`];
    if (skipped.length) {
      console.warn('Daydreams skipped:', skipped);
      parts.push(`${skipped.length} skipped (${this.summariseSkipped(skipped)})`);
    }
    if (failure) {
      parts.push(`${releaseItemIds.length - sent} not sent: ${failure} Refresh Page.`);
    }
    this.snackBar.open(parts.join('; ') + '.', 'OK', { duration: skipped.length || failure ? 10000 : 3000 });
    if (approved > 0) {
      this.clearSelection();
      await this.refreshDaydreamRows();
    }
  }

  /* Downloading the selected items' Daydreams to QA them, in zips of a bounded size: the browser fetches
     them straight from storage and builds each zip in memory. Items without a Daydream are skipped. */

  /** Daydreams per zip: about 210MB of browser memory (twice that while it's written), so big selections still split. */
  private static readonly downloadDaydreamsChunkSize = 250;
  daydreamsDownloading = false;

  private selectedDaydreamIds(): string[] {
    return this.selectedItems()
      .filter(x => x.productName === ProductName.Facade && this.hasDaydream(x))
      .map(x => x.daydreamVersion!);
  }

  canDownloadDaydreams(): boolean {
    return !this.daydreamsDownloading && this.selectedDaydreamIds().length > 0;
  }

  downloadDaydreamsTooltip(): string {
    if (this.daydreamsDownloading) return 'Downloading Daydreams…';
    if (!this.selectedReleaseItems?.length) return 'Download Daydreams';
    const count = this.selectedDaydreamIds().length;
    const skipped = this.selectedReleaseItems.length - count;
    if (!count) return 'None of the selected items has a Daydream';
    const parts = Math.ceil(count / ReleaseEditComponent.downloadDaydreamsChunkSize);
    return `Download ${count} Daydream${count === 1 ? '' : 's'}`
      + (parts > 1 ? ` in ${parts} zips` : '')
      + (skipped ? ` (skipping ${skipped} without a Daydream)` : '');
  }

  /** One zip after another, so the browser only ever holds one in memory; stops at the first that fails. */
  async downloadSelectedDaydreams(): Promise<void> {
    const ids = this.selectedDaydreamIds();
    if (!ids.length || this.daydreamsDownloading) {
      return;
    }
    const chunkSize = ReleaseEditComponent.downloadDaydreamsChunkSize;
    const parts = Math.ceil(ids.length / chunkSize);
    const timestamp = Utils.getCurrentTimestamp();
    this.daydreamsDownloading = true;
    let saved = 0;
    let downloaded = 0;
    try {
      for (let part = 1; part <= parts; part++) {
        this.snackBar.open(parts > 1 ? `Downloading Daydreams… zip ${part} of ${parts}` : 'Downloading Daydreams…');
        downloaded += await this.facadeDownloadService.download(
          this.selectedCompany?.id,
          ids.slice((part - 1) * chunkSize, part * chunkSize),
          parts > 1 ? `daydreams-${timestamp}-part-${part}-of-${parts}.zip` : `daydreams-${timestamp}.zip`);
        saved++;
      }
      this.snackBar.open(downloaded < ids.length
        ? `Downloaded ${downloaded} of ${ids.length} Daydreams${parts > 1 ? ` in ${parts} zips` : ''}; the rest are unavailable.`
        : `${ids.length} Daydream${ids.length === 1 ? '' : 's'} downloaded${parts > 1 ? ` in ${parts} zips` : ''}.`, 'OK', { duration: downloaded < ids.length ? 8000 : 3000 });
    } catch {
      this.snackBar.open(saved
        ? `Downloaded ${saved} of ${parts} zips; the next one failed. Try again with the remaining items.`
        : 'There was an error downloading the Daydreams!', 'OK', { duration: 8000 });
    } finally {
      this.daydreamsDownloading = false;
    }
  }

  /** Skipped entries read "home / master file / style template: reason"; with thousands, only the reasons and their counts fit. */
  private summariseSkipped(skipped: string[]): string {
    const counts = new Map<string, number>();
    for (const entry of skipped) {
      const at = entry.indexOf(': ');
      const reason = (at === -1 ? entry : entry.slice(at + 2)).replace(/\.$/, '');
      counts.set(reason, (counts.get(reason) ?? 0) + 1);
    }
    return [...counts].map(([reason, count]) => `${count} × ${reason}`).join(', ');
  }

  /** One facade item. The server checks the facade is Complete; anything it skips is reported. */
  requestDaydream(item: ReleaseItem): void {
    if (!this.canRequestDaydream(item) || !item.id) {
      return;
    }
    this.sendRequestDaydream([item.id], false);
  }

  /** Every item in the release whose Daydream failed, whatever the filters show. */
  failedDaydreamItemIds(): string[] {
    return (this.data?.releaseItems ?? [])
      .filter(x => !!x.id && !x.daydreamRequested && this.isDaydreamFailed(x) && this.canRequestDaydream(x))
      .map(x => x.id!);
  }

  retryFailedDaydreamsConfirm(): void {
    const ids = this.failedDaydreamItemIds();
    if (!ids.length) {
      return;
    }
    this.dialogService.open({
      title: 'Retry failed Daydreams?',
      message: `Render the Daydream again for the ${ids.length} facade${ids.length === 1 ? '' : 's'} in this release whose Daydream failed, using the release's Daydream source prompt.`,
      cancelText: 'Cancel',
      confirmText: 'Retry',
      actionIcon: 'auto_awesome',
      actionColour: 'accent'
    } as ConfirmDialogData);
    this._subscriptions.push(this.dialogService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.sendRequestDaydream(ids, false);
      }
    }));
  }

  /** The selected items, through the bulk-update bar. */
  requestDaydreamConfirm(): void {
    if (!this.selectedReleaseItems?.length) {
      return;
    }
    this.dialogService.open({
      title: 'Render Daydreams?',
      message: `Generate a Daydream for each selected facade that has been rendered in this release, using the release's Daydream source prompt. Facades not yet rendered are skipped.`,
      cancelText: 'Cancel',
      confirmText: 'Render',
      actionIcon: 'auto_awesome',
      actionColour: 'accent'
    } as ConfirmDialogData);
    this._subscriptions.push(this.dialogService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.sendRequestDaydream([...this.selectedReleaseItems], true);
      }
    }));
  }

  /** The rows are refreshed in place rather than reloaded, so the list keeps its scroll position, filters and paging. */
  private sendRequestDaydream(releaseItemIds: string[], clearSelection: boolean): void {
    const request = new BulkUpdateReleaseItemsRequest();
    request.companyId = this.selectedCompany?.id;
    request.releaseItemIds = releaseItemIds;
    request.releaseItemFieldToUpdate = ReleaseItemFieldToUpdate.RequestDaydream;
    this.situViewApiClient.bulkUpdateReleaseItems(request)
      .then((response: BulkUpdateReleaseItemsResponse) => {
        if (response.success) {
          const skipped = response.skipped?.length ?? 0;
          const requested = releaseItemIds.length - skipped;
          this.refreshDaydreamRows().then(() => this.watchDaydreams());
          if (clearSelection) {
            this.clearSelection();
          }
          this.snackBar.open(skipped
            ? `${requested} Daydream${requested === 1 ? '' : 's'} requested; ${skipped} skipped: ${response.skipped!.join(' ')}`
            : `${requested} Daydream${requested === 1 ? '' : 's'} requested.`, 'OK', { duration: skipped ? 8000 : 2000 });
        } else {
          this.snackBar.open(response.errorMessages?.join(' ') || `Could not request the Daydreams. Refresh Page.`, 'OK', { duration: 4000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Could not request the Daydreams. Refresh Page.`, 'OK', { duration: 2000 });
      });
  }

  private buildUpdateReleaseRequest(): UpdateReleaseRequest {
    const request = new UpdateReleaseRequest();
    request.companyId = this.selectedCompany?.id;
    request.releaseId = this.data?.id;
    request.name = this.data?.name;
    request.description = this.data?.description;
    request.renderReleaseStatus = this.data?.renderReleaseStatus;
    request.releaseDate = this.data?.releaseDate;
    request.skipRaytraced = this.data?.skipRaytraced;
    request.situPlan = this.data?.situPlan;
    request.situVr = this.data?.situVr;
    request.situRender = this.data?.situRender;
    request.situRaytracedPlus = this.data?.situRaytracedPlus;
    request.priority = this.data?.priority;
    request.allowedRendererPools = this.data?.allowedRendererPools;
    return request;
  }

  getViews(artifactId: string) {
    return this.logItems?.find(x => x.artifactId === artifactId)?.numberOfViews;
  }

  openLinkInNewWindow(event: MouseEvent, route: string, queryParams: { [key: string]: any }) {
    event.stopPropagation();
    const url = this.router.createUrlTree([route], { queryParams }).toString();
    window.open(url, '_blank');
  }

  openSituLinkInNewWindow(event: MouseEvent, artifactId: string, isFacade: boolean = false, showHistory: boolean = false) {
    event.stopPropagation();
    const url = this.getSituLinkUrl(artifactId, isFacade, showHistory);
    window.open(url, '_blank');
  }

  openSituFurnishInNewWindow(homeIfcFileId: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/homes/furnish/${homeIfcFileId}`]));
    window.open(url, '_blank');
  }

  furnishHomeName(home: string | undefined) {
    return (home) ? home.toLowerCase() : "";
  }

  furnishOptions(item: ReleaseItem | undefined) {
    if (item) {
      let result = (item.masterFile) ? item.masterFile.toLowerCase() : "";
      // item.options has no commas so try using homeConfig if it exists
      let options = (item.homeConfig && item.homeConfig.options) ? item.homeConfig.options : item.options?.split(',');
      if (options && options.length) {
        for (let i = 0; i < options.length; i++) {
          let s = options[i];
          if (s) {
            s = s.trim().toLowerCase();
          }
          if (s) {
            if (result) {
              result += ',';
            }
            result += s;
          }
        }
      }
      return result;
    }
    return "";
  }

  // Tooltip for a render button: names the quality and where it opens, and explains the disabled state.
  renderTooltip(label: string, artifactId: string | undefined, target: 'situVIEW' | 'situLINK'): string {
    if (!artifactId) return `${label} — not rendered yet`;
    return `Open ${label} Render in ${target}`;
  }

  /* releases */
  releases: Release[] | undefined;
  loadReleases() {
    this.loading = true;

    this.releaseService.loadReleases(this.selectedCompany?.id!)
      .then((releases: Release[] | undefined) => {
        this.releases = releases ?? [];
      })
      .finally(() => this.loading = false);
  }

  deleteConfirm() {
    this.releaseItemFieldToUpdate = ReleaseItemFieldToUpdate.Delete;
    this.dialogService.open({
      title: `Would you like to delete the selected release items?`,
      message: 'Delete the selected release Items',
      cancelText: 'Cancel',
      confirmText: 'Delete',
      actionIcon: 'priority_high',
      actionColour: 'warn'
    });
    this._subscriptions.push(this.dialogService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.setValues();
      }
    }));
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;

    this.load();
  }

  setValues() {
    if (this?.data?.situRaytracedPlus && this.releaseItemFieldToUpdate === ReleaseItemFieldToUpdate.ReleaseStatus && this.updateReleaseItemEnumValue == ReleaseStatus.StandardReviewed) {
      this.dialogService.open({
        title: 'Queue RayTracedPlus renders?',
        message: 'Have the homes been reviewed in standard res first? Excessive high quality renders may cause delays and attract additional cost.',
        cancelText: 'No',
        confirmText: 'Yes',
        actionIcon: 'check',
        actionColour: 'accent'
      } as ConfirmDialogData);
      this._subscriptions.push(this.dialogService.confirmed()
        .subscribe(confirmed => {
          if (confirmed) {
            this.bulkSetReleaseItemProperties();
          } else {
            this.loading = false;
          }
        }));
    } else {
      this.bulkSetReleaseItemProperties();
    }
  }

  clearValues() {
    this.updateReleaseItemValue = undefined;
    this.updateReleaseItemEnumValue = undefined;
  }
  
  clearSelection() {
    this.selectedReleaseItems = [];
    this.releaseItemFieldToUpdate = undefined;
    this.availableStatuses = [];
  }

  updateAvailableStatuses(): void {
    if (!this.selectedReleaseItems || this.selectedReleaseItems.length === 0) {
      this.availableStatuses = [];
      return;
    }

    // Get the statuses of all selected items
    const selectedItems = this.filteredReleaseItems.filter(item => 
      this.selectedReleaseItems.includes(item.id!)
    );
    
    const currentStatuses = selectedItems.map(item => item.releaseStatus!);
    const uniqueStatuses = [...new Set(currentStatuses)];

    // If there's only one unique status, show transitions for that status
    if (uniqueStatuses.length === 1) {
      const availableStatusesSet = this.getValidTransitionsForStatus(uniqueStatuses[0]);
      this.availableStatuses = Array.from(availableStatusesSet).map(status => ({
        value: status,
        key: this.releaseStatuses[status],
        color: this.getStatusColor(status as ReleaseStatus)
      })).sort((a, b) => a.key.localeCompare(b.key));
    } else {
      // Multiple different statuses selected - find union of all valid transitions
      const allTransitions = new Set<ReleaseStatus>();
      
      uniqueStatuses.forEach(status => {
        const validTransitions = this.getValidTransitionsForStatus(status);
        validTransitions.forEach(transition => allTransitions.add(transition));
      });

      this.availableStatuses = Array.from(allTransitions).map(status => ({
        value: status as ReleaseStatus,
        key: this.releaseStatuses[status as ReleaseStatus],
        color: this.getStatusColor(status as ReleaseStatus)
      })).sort((a, b) => a.key.localeCompare(b.key));
    }
  }

  private getValidTransitionsForStatus(status: ReleaseStatus): Set<ReleaseStatus> {
    const availableStatuses = new Set<ReleaseStatus>();

    switch (status) {
      case ReleaseStatus.RenderingHiRes:
        //allowing this reset because configs can get stuck in this state
        availableStatuses.add(ReleaseStatus.ReadyForRender);
        break;
      case ReleaseStatus.RenderingStandard:
      case ReleaseStatus.ApprovedForLive:
        break;
      case ReleaseStatus.Proposed:
      case ReleaseStatus.ReadyForRender:
      case ReleaseStatus.StandardReviewed:
        availableStatuses.add(ReleaseStatus.OnHold);
        break;
      case ReleaseStatus.RenderFailed:
        availableStatuses.add(ReleaseStatus.OnHold);
        availableStatuses.add(ReleaseStatus.ReadyForRender);
        availableStatuses.add(ReleaseStatus.RequiresAttentionSitu);
        availableStatuses.add(ReleaseStatus.RequiresAttentionVendor);
        break;
      case ReleaseStatus.ReadyForReview:
        availableStatuses.add(ReleaseStatus.OnHold);
        availableStatuses.add(ReleaseStatus.ReadyForRender);
        availableStatuses.add(ReleaseStatus.ApprovedForLive);
        availableStatuses.add(ReleaseStatus.StandardReviewed);
        availableStatuses.add(ReleaseStatus.RequiresAttentionSitu);
        availableStatuses.add(ReleaseStatus.RequiresAttentionVendor);
        break;
      case ReleaseStatus.RequiresAttentionSitu:
        availableStatuses.add(ReleaseStatus.OnHold);
        availableStatuses.add(ReleaseStatus.ReadyForRender);
        availableStatuses.add(ReleaseStatus.ReadyForReview);
        availableStatuses.add(ReleaseStatus.RequiresAttentionVendor);
        break;
      case ReleaseStatus.RequiresAttentionVendor:
        availableStatuses.add(ReleaseStatus.OnHold);
        availableStatuses.add(ReleaseStatus.ReadyForRender);
        availableStatuses.add(ReleaseStatus.ReadyForReview);
        availableStatuses.add(ReleaseStatus.RequiresAttentionSitu);
        break;
      case ReleaseStatus.ReadyForLive:
        availableStatuses.add(ReleaseStatus.OnHold);
        availableStatuses.add(ReleaseStatus.ReadyForRender);
        availableStatuses.add(ReleaseStatus.StandardReviewed);
        availableStatuses.add(ReleaseStatus.ApprovedForLive);
        availableStatuses.add(ReleaseStatus.RequiresAttentionSitu);
        availableStatuses.add(ReleaseStatus.RequiresAttentionVendor);
        break;
      case ReleaseStatus.Live:
        availableStatuses.add(ReleaseStatus.OnHold);
        availableStatuses.add(ReleaseStatus.ReadyForRender);
        availableStatuses.add(ReleaseStatus.StandardReviewed);
        availableStatuses.add(ReleaseStatus.RequiresAttentionSitu);
        availableStatuses.add(ReleaseStatus.RequiresAttentionVendor);
        break;
      case ReleaseStatus.OnHold:
        availableStatuses.add(ReleaseStatus.ReadyForRender);
        availableStatuses.add(ReleaseStatus.StandardReviewed);
        availableStatuses.add(ReleaseStatus.RequiresAttentionSitu);
        availableStatuses.add(ReleaseStatus.RequiresAttentionVendor);
        break;
    }

    return availableStatuses;
  }

  dialogRef: MatDialogRef<ReleaseStyleTemplateDialogComponent> | undefined;
  copyToStyleTemplate() {
    const homeConfigIds = this.filteredReleaseItems
      .filter(item => this.selectedReleaseItems.includes(item.id!))
      .map(x => x.homeConfigId); 

    if (!homeConfigIds || homeConfigIds.length < 1) {
      this.showMessage("Please select some home configurations");
      return;
    }

    this.dialogRef = this.dialog.open(ReleaseStyleTemplateDialogComponent, {
      panelClass: 'modal-panel',
      width: '60%',
      height: '80%',
      data: {
        companyId: this.selectedCompany?.id,
        productName: this.selectedProductName,
        homeConfigIds: homeConfigIds
      }
    });

    this._subscriptions.push(this.dialogRef.afterClosed()
      .subscribe((data: ReleaseStyleTemplateDialogComponentResultData) => {
        if (data?.releaseCount && data?.releaseItemCount) {
          this.showMessage(`${data.releaseItemCount} release items added across ${data.releaseCount} releases!`);
          this.load();
        }
      })
    );
  }

  selectAll() {
    this.selectedReleaseItems = this.filteredReleaseItems
      ?.map(x => x.id)
      .filter((id): id is string => id !== undefined) ?? [];
    
    // Update available statuses when selection changes
    this.updateAvailableStatuses();
  }

  isSelected(releaseItemId: string): boolean {
    return this.selectedReleaseItems.findIndex(x => x === releaseItemId) !== -1;
  }

  toggleSelection(releaseItemId: string) {
    const index = this.selectedReleaseItems.findIndex(x => x === releaseItemId);

    if (index === -1) {
      this.selectedReleaseItems.push(releaseItemId);
    } else {
      this.selectedReleaseItems.splice(index, 1);
    }
    
    // Update available statuses when selection changes
    this.updateAvailableStatuses();
  }

  filterHome(home: string) {
    if (home && this.selectedHome !== home) {
      this.selectedHome = home;
    } else if (!home) {
      this.selectedHome = undefined;
    }
    this.filterData();
  }

  filterMasterFile(masterFile: string) {
    if (masterFile && this.selectedMasterFile !== masterFile) {
      this.selectedMasterFile = masterFile;
    } else if (!masterFile) {
      this.selectedMasterFile = undefined;
    }
    this.filterData();
  }

  filterOption(option: string) {
    if (option && this.selectedOption !== option) {
      this.selectedOption = option;
    } else if (!option) {
      this.selectedOption = undefined;
    }
    this.filterData();
  }
  
  filterStyleTemplate(styleTemplate: string) {
    if (styleTemplate && this.selectedStyleTemplate !== styleTemplate) {
      this.selectedStyleTemplate = styleTemplate;
    } else if (!styleTemplate) {
      this.selectedStyleTemplate = undefined;
    }
    this.filterData();
  }

  filterReleaseStatus(releaseStatus: ReleaseStatus) {
    if (releaseStatus && this.selectedReleaseStatus !== releaseStatus) {
      this.selectedReleaseStatus = releaseStatus;
    } else if (!releaseStatus) {
      this.selectedReleaseStatus = undefined;
    }
    this.filterData();
  }

  filterProductName(productName: ProductName) {
    if (productName !== undefined && this.selectedProductName !== productName) {
      this.selectedProductName = productName;
    } else if (productName === undefined) {
      this.selectedProductName = undefined;
    }
    this.filterData();
  }

  filterComments(shouldFilter: boolean) {
    this.commentsFilter = shouldFilter;
    this.filterData();
  }

  filterTickets(shouldFilter: boolean) {
    this.ticketFilter = shouldFilter;
    this.filterData();
  }

  filterStandard(shouldFilter: boolean) {
    this.standardFilter = shouldFilter;
    this.filterData();
  }

  filterDraft(shouldFilter: boolean) {
    this.draftFilter = shouldFilter;
    this.filterData();
  }

  filterHiRes(shouldFilter: boolean) {
    this.hiResFilter = shouldFilter;
    this.filterData();
  }

  filterIssue(issue: string) {
    if (issue && this.issueFilter !== issue) {
      this.issueFilter = issue;
    } else if (!issue) {
      this.issueFilter = undefined;
    }
    this.filterData();
  }

  filterViewed(shouldFilter: boolean) {
    this.viewedFilter = shouldFilter;
    this.filterData();
  }

  downloadSelectedFacades(hiRes: boolean = false) {
    let artifactIds;
    if (hiRes) {
      artifactIds = this.filteredReleaseItems
      .filter(item => this.selectedReleaseItems.includes(item.id!))
      .map(x => x.higherResVersion ?? "")
      .filter(x => x);
  
      if (!artifactIds || artifactIds.length < 1) {
        this.showMessage("Please select some rendered HiRes release items");
        return;
      }
    } else {
      artifactIds = this.filteredReleaseItems
      .filter(item => this.selectedReleaseItems.includes(item.id!))
      .map(x => x.latestVersion ?? "")
      .filter(x => x);
  
      if (!artifactIds || artifactIds.length < 1) {
        this.showMessage("Please select some rendered release items");
        return;
      }
    }

    // The browser fetches and zips the files itself, so keep a message up until it's done
    const preparing = this.snackBar.open(`Preparing ${artifactIds.length} facade${artifactIds.length === 1 ? '' : 's'} for download...`);

    this.facadeDownloadService.download(this.selectedCompany?.id, artifactIds)
      .then((downloaded: number) => {
        preparing.dismiss();
        if (downloaded < artifactIds.length) {
          this.showMessage(`Downloaded ${downloaded} of ${artifactIds.length} facades; the rest are unavailable.`);
        }
      })
      .catch((err: any) => {
        preparing.dismiss();
        this.showMessage("There was an error downloading the file!");
      });
  }

  downloadCSV() {
    if (!this.filteredReleaseItems) {
      return;
    }

    const csvData = this.convertToCSV(this.filteredReleaseItems);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const timestamp = Utils.getCurrentTimestamp();
    const fileName = `release_items_${timestamp}.csv`;

    // For IE 11
    if ((navigator as any).msSaveBlob) {
      (navigator as any).msSaveBlob(blob, fileName);
    } else {
      // Modern browsers
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  private convertToCSV(releaseItems: ReleaseItem[]): string {
    let str = '';
    let row = '';

    const headers = ['Home', 'Link', 'HiRes Link', 'Product Name', 'Status', 'Notes'];
    row = headers.join(',') + '\r\n';
    str += row;

    releaseItems.forEach((item: ReleaseItem) => {
      let line = '';
      line += `${item.home} / ${item.masterFile} / ${item.options?.split(",").join("~")} / ${item.styleTemplate},`;
      line += `${this.getSituLinkUrl(item.latestVersion!, item.productName === ProductName.Facade)},`;
      line += `${this.getSituLinkUrl(item.higherResVersion!, item.productName === ProductName.Facade)},`;
      line += `${this.productNames[item.productName!]},`;
      line += `${this.releaseStatuses[item.releaseStatus!]},`;
      line += `${item.comments ? item.comments : ""},`;
      str += line + '\r\n';
    });

    return str;
  }

  getSituLinkUrl(artifactId: string, isFacade: boolean = false, showHistory: boolean = false): string {
    if (!artifactId) {
      return "";
    }

    const baseUrl = this.situLinkBaseUrl 
        ? `https://${isFacade ? 'facade' : 'view'}.${this.situLinkBaseUrl}`
        : this.situPortalBaseUrl!;
    
    const url = new URL(baseUrl);
    url.pathname = artifactId;
    if (showHistory) url.searchParams.append("history", "true")

    return url.toString();
  }

  uploadCSV() {
    if (!this.data?.name || !this.selectedCompany?.code) {
      this.showMessage("Release data not loaded. Please wait and try again.");
      return;
    }

    // Show confirmation dialog before proceeding
    this.dialogService.open({
      title: 'Confirm Release Items Import',
      message: `Are you sure you want to import release items into ${this.selectedCompany.code}'s "${this.data.name}" release?`,
      cancelText: 'Cancel',
      confirmText: 'Import Release Items',
      actionIcon: 'file_upload',
      actionColour: 'primary'
    } as ConfirmDialogData);

    this._subscriptions.push(this.dialogService.confirmed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.proceedWithCSVUpload();
        }
      }));
  }

  private proceedWithCSVUpload() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.style.display = 'none';
    
    fileInput.onchange = (event: any) => {
      const file = event.target.files[0];
      if (!file) return;
      
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (fileExt !== 'csv') {
        this.showMessage("Only CSV files are allowed");
        return;
      }
      
      this.loading = true;
      
      // Create FileParameter object for the API
      const fileParameter = {
        data: file,
        fileName: file.name
      };
      
      this.situViewApiClient.importReleaseItemsFromCsv(fileParameter, this.selectedCompany?.id, this.id)
        .then((response: any) => {
          this.loading = false;

          const errors: string[] = response.errors ?? [];
          const warnings: string[] = response.warnings ?? [];

          let message = `Import completed! `;
          message += `Processed: ${response.totalProcessed || 0}, `;
          message += `Imported: ${response.totalImported || 0}, `;
          message += `Skipped: ${response.totalSkipped || 0}`;

          if (errors.length > 0) {
            message += `, Failed: ${errors.length}`;
          }

          if (response.accessoryBundlesCreated > 0) {
            message += `, Accessory Bundles: ${response.accessoryBundlesCreated}`;
          }

          this.showMessage(message);

          // Surface the per-line reasons the server returns; without this the user
          // can't tell why specific lines failed.
          if (errors.length > 0 || warnings.length > 0) {
            this.showImportReport(message, errors, warnings);
          }

          this.load();
        })
        .catch((error: any) => {
          this.loading = false;
          this.showMessage("Error importing CSV file!");
        });
    };
    
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  }

  private showImportReport(summary: string, errors: string[], warnings: string[]) {
    // Cap the list shown in the dialog; the full detail is always logged for
    // large imports so nothing is lost.
    const maxLines = 100;
    const lines: string[] = [];

    if (errors.length > 0) {
      lines.push(`Failed lines (${errors.length}):`);
      lines.push(...errors.slice(0, maxLines));
      if (errors.length > maxLines) {
        lines.push(`…and ${errors.length - maxLines} more (see browser console).`);
      }
      console.warn('CSV import errors:', errors);
    }

    if (warnings.length > 0) {
      if (lines.length > 0) {
        lines.push('');
      }
      lines.push(`Warnings (${warnings.length}):`);
      lines.push(...warnings.slice(0, maxLines));
      if (warnings.length > maxLines) {
        lines.push(`…and ${warnings.length - maxLines} more (see browser console).`);
      }
      console.warn('CSV import warnings:', warnings);
    }

    this.dialog.open(ConfirmDialogComponent, {
      width: '640px',
      data: {
        title: 'Import Report',
        message: `${summary}\n\n${lines.join('\n')}`,
        confirmText: '',
        actionIcon: '',
        actionColour: 'primary',
        cancelText: 'Close'
      } as ConfirmDialogData
    });
  }


  downloadJSON() {
    const jsonData = JSON.stringify(this.filteredReleaseItems?.map(x => {
      return {
        "homeConfig": {
          "home": x.home,
          "masterFile": x.masterFile,
          "options": x.options ? x.options?.split(',') : [],
          "themes": []
        },
        "styleChoicesConfigName": x.styleTemplate,
        "productName": this.productNames[x.productName!],
        "releaseStatus": this.releaseStatuses[x.releaseStatus!]
      };
    }));
    const blob = new Blob([jsonData], { type: 'application/json;charset=utf-8;' });
    const timestamp = Utils.getCurrentTimestamp();
    const fileName = `release_items_${timestamp}.json`;

    // For IE 11
    if ((navigator as any).msSaveBlob) {
      (navigator as any).msSaveBlob(blob, fileName);
    } else {
      // Modern browsers
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  releaseStats: {
    uniqueHomeConfigs: number;
    uniqueHomes: number;
    uniqueMasterFiles: number;
    uniqueStyleTemplates: number;
    uniqueOptions: number;
    totalItems: number;
    totalRenderTimeRemaining: number;
  } = {
    uniqueHomeConfigs: 0,
    uniqueHomes: 0,
    uniqueMasterFiles: 0,
    uniqueStyleTemplates: 0,
    uniqueOptions: 0,
    totalItems: 0,
    totalRenderTimeRemaining: 0
  };
  
  // Call this method from filterData() after updating filteredReleaseItems
  calculateReleaseStats() {
    // Initialize empty Sets to track unique values
    const uniqueHomeConfigs = new Set<string>();
    const uniqueHomes = new Set<string>();
    const uniqueMasterFiles = new Set<string>();
    const uniqueStyleTemplates = new Set<string>();
    const uniqueOptions = new Set<string>();
    let totalRenderTimeRemaining = 0;
    
    // Iterate through filtered items to collect unique values
    this.filteredReleaseItems.forEach(item => {
      if (item.homeConfigId) uniqueHomeConfigs.add(item.homeConfigId);
      if (item.home) uniqueHomes.add(item.home);
      if (item.masterFile) uniqueMasterFiles.add(item.masterFile);
      if (item.styleTemplate) uniqueStyleTemplates.add(item.styleTemplate);
      
      // For options, we need to split them since they're stored as comma-separated values
      if (item.options) {
        item.options.split(',').forEach(option => {
          option = option.trim();
          if (option) uniqueOptions.add(option);
        });
      }

      if (item.releaseStatus !== ReleaseStatus.Live && item.releaseStatus !== ReleaseStatus.ApprovedForLive) {
        let renderTimeEstimate = 0;
        
        switch (item.productName) {
          case ProductName.Panorama:
            let panoEstimate = 0;
            if (!item?.latestVersion) panoEstimate += 2;
            if (!item?.higherResVersion) panoEstimate += 25;
            if (this.data?.situPlan && !item.situPlan) panoEstimate += 3;
            if (this.data?.situRender && !item.situRender) panoEstimate += 1;
            if (this.data?.situRaytracedPlus && !item.highestResVersion) panoEstimate += 466;
            renderTimeEstimate = panoEstimate;
            break;
          case ProductName.Facade:
            let facadeEstimate = 0;
            if (!item?.latestVersion) facadeEstimate += 5;
            if (!item?.higherResVersion) facadeEstimate += 10;
            renderTimeEstimate = facadeEstimate;
            break;
          default:
            renderTimeEstimate = 5;
        }
        
        totalRenderTimeRemaining += renderTimeEstimate;
      }
    });
    
    // Update the stats object
    this.releaseStats = {
      uniqueHomeConfigs: uniqueHomeConfigs.size,
      uniqueHomes: uniqueHomes.size,
      uniqueMasterFiles: uniqueMasterFiles.size,
      uniqueStyleTemplates: uniqueStyleTemplates.size,
      uniqueOptions: uniqueOptions.size,
      totalItems: this.filteredReleaseItems.length,
      totalRenderTimeRemaining: totalRenderTimeRemaining
    };
  }

  formatRenderTime(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 
        ? `${hours}h ${remainingMinutes}m` 
        : `${hours}h`;
    }
  }

  /* filtering */
  filterData() {
    this.pageLength = this.pageSize;
    this.filteredReleaseItems = this.filterReleaseItems(this.data?.releaseItems!, {
        updatedByName: this.searchTerm,
        comments: this.searchTerm,
        supportTask: this.searchTerm,
        home: this.selectedHome,
        masterFile: this.selectedMasterFile,
        options: this.selectedOption,
        styleTemplate: this.selectedStyleTemplate,
        releaseStatus: this.selectedReleaseStatus,
        productName: this.selectedProductName,
        hasComments: this.commentsFilter ? true : undefined,
        hasTickets: this.ticketFilter ? true : undefined,
        hasStandard: this.standardFilter ? true : undefined,
        hasDraft: this.draftFilter ? true : undefined,
        hasHiRes: this.hiResFilter ? true : undefined,
        releaseIssue: this.issueFilter,
        isViewed: this.viewedFilter ? true : undefined,
    });
    
    // Update selectedReleaseItems to only include items in the filtered results
    const filteredItemIds = this.filteredReleaseItems
      .map(item => item.id)
      .filter((id): id is string => id !== undefined);
      
    this.selectedReleaseItems = this.selectedReleaseItems
      .filter(selectedId => filteredItemIds.includes(selectedId));
      
    this.sortData();
    this.calculateStatusPercentages();
    this.calculateReleaseStats();
    this.updateParams();
  }
  private filterReleaseItems(items: ReleaseItem[], filter: {
    updatedByName?: string,
    home?: string,
    masterFile?: string,
    options?: string,
    styleTemplate?: string,
    supportTask?: string,
    comments?: string,
    releaseStatus?: ReleaseStatus,
    productName?: ProductName,
    hasComments?: boolean,
    hasTickets?: boolean,
    hasStandard?: boolean,
    hasDraft?: boolean,
    hasHiRes?: boolean,
    releaseIssue?: string,
    isViewed?: boolean
  }): ReleaseItem[] {
      return items.filter(item => {
          return  (
                    (filter.updatedByName === undefined ||
                      item.home?.includes(filter.updatedByName) || 
                      item.masterFile?.includes(filter.updatedByName) || 
                      item.options?.includes(filter.updatedByName) || 
                      item.styleTemplate?.includes(filter.updatedByName)
                    ) ||
                    (filter.comments === undefined || item.comments?.includes(filter.comments)) ||
                    (filter.supportTask === undefined || item.supportTask?.includes(filter.supportTask))
                  ) &&
                  (filter.home === undefined || item.home === filter.home) &&
                  (filter.masterFile === undefined || item.masterFile === filter.masterFile) &&
                  (filter.options === undefined || item.options?.includes(filter.options)) &&
                  (filter.styleTemplate === undefined || item.styleTemplate === filter.styleTemplate) &&
                  (filter.releaseStatus === undefined || item.releaseStatus === filter.releaseStatus) &&
                  (filter.productName === undefined || item.productName === filter.productName) &&
                  (filter.hasComments === undefined || item.comments) &&
                  (filter.hasTickets === undefined || item.supportTask) &&
                  (filter.hasStandard === undefined || item.latestVersion) &&
                  (filter.hasDraft === undefined || item.rayTracedDraftVersion) &&
                  (filter.hasHiRes === undefined || item.higherResVersion) &&
                  (filter.isViewed === undefined || item.lastViewedBy) &&
                  (filter.releaseIssue === undefined || item.releaseIssueReleaseItems?.find(x => x.releaseIssueId == filter.releaseIssue));
      });
  }
  /* filtering */

  /* sorting */
  sortCriteria: string = 'home'; // or any default sorting field
  sortOrder: 'asc' | 'desc' = 'asc';
  sortData() {
    if (this.sortCriteria) {
      this.filteredReleaseItems.sort((a, b) => {
        let valueA = this.getFieldValue(a, this.sortCriteria);
        let valueB = this.getFieldValue(b, this.sortCriteria);
  
        if (valueA < valueB) {
          return this.sortOrder === 'asc' ? -1 : 1;
        } else if (valueA > valueB) {
          return this.sortOrder === 'asc' ? 1 : -1;
        } else {
          return 0;
        }
      });
    }
  }

  getEnumTextValue(enumType: any, value: number): string {
    return enumType[value];
  }

  getSortCriteriaName(field: string){
    switch (field) {
      case 'home':
        return 'Home';
      case 'masterFile':
        return 'MasterFile';
      case 'option':
        return 'Options';
      case 'styleTemplate':
        return 'Style Template';
      case 'releaseStatus':
        return 'Release Status';
      case 'renderType':
        return 'Render Type';
      default:
        return '';
    }
  }
  
  getFieldValue(item: ReleaseItem, field: string) {
    switch (field) {
      case 'home':
        return item.home ?? '';
      case 'masterFile':
        return item.masterFile ?? '';
      case 'option':
        return item.options ?? '';
      case 'styleTemplate':
        return item.styleTemplate ?? '';
      case 'releaseStatus':
        return item.releaseStatus !== undefined ? this.getEnumTextValue(ReleaseStatus, item.releaseStatus) : '';
      case 'renderType':
        return item.productName !== undefined ? this.getEnumTextValue(ProductName, item.productName) : '';
      default:
        return '';
    }
  }
  /* sorting */

  clearSearch() {
    this.searchTerm = undefined;
  }

  statusPercentages: { status: ReleaseStatus, percentage: number, count: number }[] = [];
  releaseOverviewData: { inFlightCount: number, upcomingCount: number, completedCount: number } = {
    inFlightCount: 0,
    upcomingCount: 0,
    completedCount: 0
  };
  
  // Chart data
  statusChartData: any[] = [];
  statusChartLabels: string[] = [];
  statusChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    }
  };
  statusChartColors: string[] = [];
  calculateStatusPercentages() {
    if (!this.filteredReleaseItems || this.filteredReleaseItems.length === 0) {
      return;
    }
  
    const totalItems = this.filteredReleaseItems.length;
    const statusCounts: { [key: string]: number } = {};
    const minDisplayPercentage = 5; // Minimum display percentage for any segment
  
    // Count the occurrences of each status
    this.filteredReleaseItems.forEach(item => {
      if (item.releaseStatus !== undefined) {
        const status = item.releaseStatus as unknown as string;
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      }
    });
  
    // Calculate initial raw percentages
    let totalRawPercentage = 0;
    const rawPercentages = Object.keys(statusCounts).map(status => {
      const count = statusCounts[status] || 0;
      const percentage = (count / totalItems) * 100;
      totalRawPercentage += percentage;
      return {
        status: parseInt(status, 10) as ReleaseStatus,
        rawPercentage: percentage,
        count: count
      };
    });
  
    // Apply minimum display percentage and adjust the rest
    let totalRoundedPercentage = 0;
    let adjustedPercentages = rawPercentages.map((status, index, array) => {
      let percentage = Math.max(Math.round(status.rawPercentage), minDisplayPercentage);
      totalRoundedPercentage += percentage;
  
      // If it's the last element, adjust to ensure totalRoundedPercentage is 100%
      if (index === array.length - 1 && totalRoundedPercentage !== 100) {
        percentage += 100 - totalRoundedPercentage;
      }
  
      return {
        status: status.status,
        percentage: percentage,
        count: status.count
      };
    });
  
    // Check if the rounding caused the total to exceed 100%
    let totalAdjustedPercentage = adjustedPercentages.reduce((sum, status) => sum + status.percentage, 0);
  
    if (totalAdjustedPercentage !== 100) {
      const difference = 100 - totalAdjustedPercentage;
  
      // Adjust the percentages proportionally
      adjustedPercentages = adjustedPercentages.map((status, index) => {
        if (index === adjustedPercentages.length - 1) {
          status.percentage += difference; // Adjust the last element
        } else {
          const adjustment = Math.round((status.percentage / totalAdjustedPercentage) * difference);
          status.percentage += adjustment;
          totalAdjustedPercentage += adjustment;
        }
        return status;
      });
    }
  
    this.statusPercentages = adjustedPercentages;
    
    // Update chart data
    this.updateChartData();
  }
  
  private updateChartData(): void {
    // Update status chart
    this.statusChartLabels = this.statusPercentages.map(sp => this.releaseStatuses[sp.status]);
    this.statusChartData = [{
      data: this.statusPercentages.map(sp => sp.count)
    }];
    this.statusChartColors = this.statusPercentages.map(sp => this.getStatusColor(sp.status));
    
    // Update chart options - remove onClick to allow chart component's (chartClick) to work
    this.statusChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        }
      }
    };
  }
  
  private bulkSetReleaseItemProperties() {
    let request = new BulkUpdateReleaseItemsRequest();
    request.releaseItemIds = this.selectedReleaseItems;
    request.companyId = this.selectedCompany?.id;
    request.releaseItemFieldToUpdate = this.releaseItemFieldToUpdate;
    switch (this.releaseItemFieldToUpdate) {
      case ReleaseItemFieldToUpdate.Comments:
        request.comments = this.updateReleaseItemValue;
        break;
      case ReleaseItemFieldToUpdate.ReleaseStatus:
        request.releaseStatus = this.updateReleaseItemEnumValue;
        break;
      case ReleaseItemFieldToUpdate.SupportTicket:
        request.supportTicket = this.updateReleaseItemValue;
        break;
      case ReleaseItemFieldToUpdate.AssignToReleaseIssue:
        request.releaseIssueId = this.updateReleaseItemValue;
        break;
      case ReleaseItemFieldToUpdate.Delete:
        break;
    }

    this.situViewApiClient.bulkUpdateReleaseItems(request)
      .then((response: BulkUpdateReleaseItemsResponse) => {
        if (response.success) {
          this.load();
          this.clearValues();
          this.clearSelection();
        } else {
          this.snackBar.open(`Could not update the release items. Refresh Page.`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Could not update the release items. Refresh Page.`, 'OK', { duration: 2000 });
      });
  }

  setReleaseItemViewed(releaseItemId: string) {
    let request = new SetReleaseItemViewedRequest();
    request.companyId = this.selectedCompany?.id;
    request.releaseId = this.id;
    request.releaseItemId = releaseItemId;

    this.situViewApiClient.setReleaseItemViewed(request)
      .then((response: SetReleaseItemViewedResponse) => {
        if (response?.success) {
          this.filteredReleaseItems = this.filteredReleaseItems.map(item => {
            if (item.id === releaseItemId) {
              item.lastViewedBy = this.authService.getUserId();
              item.lastViewedName = this.authService.getUserName();
              item.dateLastViewed = new Date();
            }
            return item;
          });
          if (this.data?.releaseItems) {
            this.data.releaseItems = this.data.releaseItems.map(item => {
              if (item.id === releaseItemId) {
                item.lastViewedBy = this.authService.getUserId();
                item.lastViewedName = this.authService.getUserName();
                item.dateLastViewed = new Date();
              }
              return item;
            });
          }
        }
      })
      .catch((err: any) => {})
      .finally(() => {});
  }

  canScanForProductUsage(item: ReleaseItem): boolean {
    if (!item?.latestVersion) return false;
    return item.productName === ProductName.Panorama
      || item.productName === ProductName.RayTracedDraft
      || item.productName === ProductName.RayTraced
      || item.productName === ProductName.RayTracedPlus;
  }

  scanForProductUsage(event: Event, item: ReleaseItem) {
    event.stopPropagation();
    if (!item?.latestVersion || this.scanningArtifactId) return;
    const artifactId = item.latestVersion;
    this.scanningArtifactId = artifactId;

    const request = new LoadProductUsageFromArtifactRequest({ renderedOutputId: artifactId });
    this.situViewApiClient.loadProductUsageFromArtifact(request)
      .then(response => {
        if (response?.success) {
          const snack = this.snackBar.open('Product usage scanned', 'View', { duration: 5000 });
          snack.onAction().subscribe(() => {
            this.router.navigate(['/products/discover'], { queryParams: { renderedOutputId: artifactId } });
          });
        } else {
          const message = response?.errorMessages?.join(', ') || 'Failed to scan for product usage';
          this.snackBar.open(message, 'OK', { duration: 3000 });
        }
      })
      .catch(() => {
        this.snackBar.open('Sorry! There was an issue scanning for product usage. Please try again.', 'OK', { duration: 3000 });
      })
      .finally(() => {
        this.scanningArtifactId = undefined;
      });
  }

  showReleaseIssues: boolean = false;
  releaseIssuePriorities = ReleaseIssuePriority;
  releaseIssueStatuses = ReleaseIssueStatus;
  releaseIssues: ReleaseIssue[] = [];
  private loadReleaseIssues() {
    this.loading = true;
    let request = new ListReleaseIssuesRequest();
    //request.companyId = this.selectedCompany?.id;
    request.releaseId = this.id;
    request.includeStats = true;

    this.situViewApiClient.listReleaseIssues(request)
      .then((response: ListReleaseIssuesResponse) => {
        if (response.success && response.items) {
          this.releaseIssues = response.items;
        }
      })
      .catch((err: any) => {

      })
      .finally(() => {
        this.loading = false;
      });
  }

  private loadReleaseOverviewData(): void {
    if (this.released || this.top100) {
      // Don't load overview data for special releases
      return;
    }

    const request = new ListRenderingReleaseItemsRequest();
    request.releaseId = this.id;
    
    this.situViewApiClient.listRenderingReleaseItems(request)
      .then((response: ListRenderingReleaseItemsResponse) => {
        if (response.success) {
          this.releaseOverviewData = {
            inFlightCount: response.inFlightItems?.length || 0,
            upcomingCount: response.upcomingItems?.length || 0,
            completedCount: response.recentlyCompletedItems?.length || 0
          };
        }
      })
      .catch((err: any) => {
        console.error('Error loading release overview data:', err);
      });
  }

  releaseIssueDialogRef: MatDialogRef<ReleaseIssueDialogComponent> | undefined;
  addEditReleaseIssue(releaseIssueId?: string) {
    this.releaseIssueDialogRef = this.dialog.open(ReleaseIssueDialogComponent, {
      panelClass: 'modal-panel',
      width: '60%',
      height: '80%',
      data: {
        isSituUser: this.isSituUser,
        companyId: this.selectedCompany?.id,
        releaseIssueId: releaseIssueId,
        releaseId: this.id
      }
    });

    this._subscriptions.push(this.releaseIssueDialogRef.afterClosed()
      .subscribe((data: ReleaseIssueDialogComponentResultData) => {
        if (data?.refreshNeeded) {
          this.loadReleaseIssues();
        }
      })
    );
  }

  //selectedCard: string | undefined;
  onCardSelectClick(releaseIssueId: string | undefined) { 
    if (releaseIssueId !== this.issueFilter) {
      this.issueFilter = releaseIssueId;
    } else {
      this.issueFilter = undefined;
    }
    this.filterData();
  }

  private load(): void {
    this.loadReleaseIssues();
    this.loadReleaseOverviewData();
    if (this.released) {
      if (this.top100) {
        this.breadcrumbService.setParam('releaseName', "Top 100");
        let request = new GetMostPopularRenderedHomesByBuilderRequest();
        request.ownerId = this.selectedCompany?.id;
        request.pageNumber = 1;
        request.pageSize = 50;
        request.daysPreviousToSearch = 7;
    
        this.analyticsClient.getMostPopularRenderedHomesByBuilder(request)
          .then((response: GetMostPopularRenderedHomesByBuilderResponse) => {
            if (response.success) {
              this.viewControls.totalItems = response.totalItems!;
              let release = new Release();
              release.name = "Top 100";
              release.description = "Top 100 homes viewed in the past week";
              let releasedItems = new Array<ReleaseItem>();
              this.logItems = response.logItems;
              response.logItems?.forEach((renderedOutput: ViewMostPopularHomeLogItem) => {
                let releaseItem = new ReleaseItem();
                releaseItem.id = renderedOutput.artifactId;
                releaseItem.home = renderedOutput.homeName;
                releaseItem.releaseStatus = ReleaseStatus.Live;
                releaseItem.masterFile = renderedOutput.facade;
                releaseItem.options = renderedOutput.options;
                // releaseItem.homeConfig = renderedOutput.homeConfig;
                // releaseItem.homeConfigId = renderedOutput.homeConfigId;
                releaseItem.styleTemplate = renderedOutput.themes;
                releaseItem.latestVersion = renderedOutput.artifactId;
                releaseItem.productName = renderedOutput.productName as any;
                releasedItems.push(releaseItem);
              });

              release.releaseItems = releasedItems;
              this.data = release;
              this.filterData();
            } else {
              this.snackBar.open(`Could not fetch the release. Refresh Page.`, 'OK', { duration: 2000 });
            }
          })
          .catch((err: any) => {
            this.snackBar.open(`Could not fetch the release. Refresh Page.`, 'OK', { duration: 2000 });
          });
      } else {
        this.breadcrumbService.setParam('releaseName', "complete");
        let request = new ListRenderedOutputsRequest();
        request.companyId = this.selectedCompany?.id;
        request.isProduction = true;
        request.pageNumber = this.viewControls.pageIndex;
        request.pageSize = this.viewControls.pageSize;
        request.orderByHome = true;
        request.productName = ProductName.Panorama;
        request.expandProducts = true;
        if (this.searchTerm) request.containsWords = [this.searchTerm];
    
        this.situViewApiClient.listRenderedOutputs(request)
          .then((response: ListRenderedOutputsResponse) => {
            if (response.success) {
              this.viewControls.totalItems = response.totalItems!;
              let release = new Release();
              release.name = "Released";
              release.description = "Items that have already been released";
              let releasedItems = new Array<ReleaseItem>();
              response.renderedOutputs?.forEach((renderedOutput: RenderedOutput) => {
                let releaseItem = new ReleaseItem();
                releaseItem.id = renderedOutput.id;
                releaseItem.home = renderedOutput.homeConfig!.home;
                releaseItem.releaseStatus = ReleaseStatus.Live;
                releaseItem.masterFile = renderedOutput.homeConfig!.masterFile;
                releaseItem.options = renderedOutput.homeConfig!.options?.join(" ");
                releaseItem.homeConfig = renderedOutput.homeConfig;
                releaseItem.homeConfigId = renderedOutput.homeConfigId;
                releaseItem.styleTemplate = renderedOutput.styleConfigTemplate?.name;
                releaseItem.latestVersion = renderedOutput.id;
                releaseItem.productName = renderedOutput.productName;
                releasedItems.push(releaseItem);
              });
  
              release.releaseItems = releasedItems;
              this.data = release;
              this.filterData();
            } else {
              this.snackBar.open(`Could not fetch the release. Refresh Page.`, 'OK', { duration: 2000 });
            }
          })
          .catch((err: any) => {
            this.snackBar.open(`Could not fetch the release. Refresh Page.`, 'OK', { duration: 2000 });
          });
      }
    } else {
      let request = new GetReleaseRequest();
      request.releaseId = this.id;
      request.companyId = this.selectedCompany?.id;
  
      this.situViewApiClient.getRelease(request)
        .then((response: GetReleaseResponse) => {
          if (response.success) {
            if (response?.release?.companyId != this.selectedCompany?.id && response?.release?.companyId) {
              if (!this.authService.isAuthorizedForCompanyById(response.release.companyId)) {
                this.showMessage(`Sorry! Your are not authorized for company ${response.release.companyId}.`);
                this.router.navigateByUrl('/unauthorized');
                return;
              }
    
              this.companyService.getCompanyById(response.release.companyId)
                .then((company) => {
                  if (!company) return;
    
                  this.stateService.state = {
                    ...this.stateService.state,
                    company
                  };
                });
            }
            this.breadcrumbService.setParam('releaseName', response?.release?.name);
            this.data = response.release;
            this.loadDaydreamSource();
            this.filterData();
            this.watchDaydreams();
          } else {
            this.snackBar.open(`Could not fetch the release. Refresh Page.`, 'OK', { duration: 2000 });
          }
        })
        .catch((err: any) => {
          this.snackBar.open(`Could not fetch the release. Refresh Page.`, 'OK', { duration: 2000 });
        });
    }
  };

  private showMessage(message: string) {
    this.snackBar.open(message, 'Dismiss', {
      duration: 3000
    });
  }

  private updateParams() {
    const queryParams: { [key: string]: any } = {};

    if (this.searchTerm) {
      queryParams.search = this.searchTerm;
    }
    if (this.selectedHome) {
      queryParams.home = this.selectedHome;
    }
    if (this.selectedMasterFile) {
      queryParams.masterfile = this.selectedMasterFile;
    }
    if (this.selectedOption) {
      queryParams.options = this.selectedOption;
    }
    if (this.selectedStyleTemplate) {
      queryParams.template = this.selectedStyleTemplate;
    }
    if (this.selectedReleaseStatus !== undefined) {
      queryParams.status = this.selectedReleaseStatus;
    }
    if (this.selectedProductName !== undefined) {
      queryParams.type = this.selectedProductName;
    }
    if (this.commentsFilter) {
      queryParams[this.CommentsParamName] = this.commentsFilter.toString();
    }
    if (this.ticketFilter) {
      queryParams[this.TicketsParamName] = this.ticketFilter.toString();
    }
    if (this.standardFilter) {
      queryParams[this.StandardParamName] = this.standardFilter.toString();
    }
    if (this.draftFilter) {
      queryParams[this.DraftParamName] = this.draftFilter.toString();
    }
    if (this.hiResFilter) {
      queryParams[this.HiResParamName] = this.hiResFilter.toString();
    }
    if (this.viewedFilter) {
      queryParams[this.ViewedParamName] = this.viewedFilter.toString();
    }
    if (this.issueFilter) {
      queryParams.issue = this.issueFilter;
    }

    const urlTree = this.router.createUrlTree([], {
      queryParamsHandling: "merge",
      preserveFragment: true,
      queryParams: queryParams
    });

    if (!this.searchTerm) {
      delete urlTree.queryParams[this.SearchParamName];
    }
    if (!this.selectedHome) {
      delete urlTree.queryParams[this.HomeParamName];
    }
    if (!this.selectedMasterFile) {
      delete urlTree.queryParams[this.MasterFileParamName];
    }
    if (!this.selectedOption) {
      delete urlTree.queryParams[this.OptionsParamName];
    }
    if (!this.selectedStyleTemplate) {
      delete urlTree.queryParams[this.StyleTemplateParamName];
    }
    if (this.selectedReleaseStatus === undefined) {
      delete urlTree.queryParams[this.StatusParamName];
    }
    if (this.selectedProductName === undefined) {
      delete urlTree.queryParams[this.RenderTypeParamName];
    }
    if (!this.commentsFilter) {
      delete urlTree.queryParams[this.CommentsParamName];
    }
    if (!this.ticketFilter) {
      delete urlTree.queryParams[this.TicketsParamName];
    }
    if (!this.standardFilter) {
      delete urlTree.queryParams[this.StandardParamName];
    }
    if (!this.draftFilter) {
      delete urlTree.queryParams[this.DraftParamName];
    }
    if (!this.hiResFilter)  {
      delete urlTree.queryParams[this.HiResParamName];
    }
    if (!this.viewedFilter)  {
      delete urlTree.queryParams[this.ViewedParamName];
    }
    if (!this.issueFilter)  {
      delete urlTree.queryParams[this.IssueParamName];
    }
  
    this.router.navigateByUrl(urlTree);
  }
}
